'use client'

import { useEffect, useRef, useState } from 'react'
import {
  RUN_SAFE_VIDEO_COORDINATION_EVENT,
  type RunSafeVideoCoordinationDetail,
} from './run-safe-in-the-dark-video-coordination'

const YOUTUBE_VIDEO_ID = 'VdoSWTjul5E'
const YOUTUBE_EMBED_BASE_SRC = `https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${YOUTUBE_VIDEO_ID}&controls=0&modestbranding=1&playsinline=1&rel=0&enablejsapi=1`

export const RunSafeInTheDarkHeroVideo = ({
  isMobile,
}: {
  isMobile: boolean
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const isSelfInViewRef = useRef(true)
  const isFounderSaidActiveRef = useRef(false)
  // Safari (ITP) cần đúng tham số `origin` để player khởi tạo, nếu không video sẽ kẹt ở màn hình loading.
  const [embedSrc] = useState(() =>
    typeof window === 'undefined'
      ? null
      : `${YOUTUBE_EMBED_BASE_SRC}&origin=${encodeURIComponent(window.location.origin)}`
  )

  useEffect(() => {
    const applyPlaybackState = () => {
      const shouldPlay = isSelfInViewRef.current && !isFounderSaidActiveRef.current

      if (isMobile) {
        if (shouldPlay) {
          videoRef.current?.play().catch(() => {})
        } else {
          videoRef.current?.pause()
        }
        return
      }

      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({
          event: 'command',
          func: shouldPlay ? 'playVideo' : 'pauseVideo',
          args: [],
        }),
        '*'
      )
    }

    // Chỉ play hero khi chính nó đang trong viewport, tránh chạy ngầm lãng phí khi đã cuộn qua khỏi.
    const visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        isSelfInViewRef.current = entry.isIntersecting
        applyPlaybackState()
      },
      { threshold: 0 }
    )

    if (containerRef.current) {
      visibilityObserver.observe(containerRef.current)
    }

    const handleCoordination = (
      event: CustomEvent<RunSafeVideoCoordinationDetail>
    ) => {
      if (event.detail.source === 'hero') return

      isFounderSaidActiveRef.current = event.detail.active
      applyPlaybackState()
    }

    window.addEventListener(
      RUN_SAFE_VIDEO_COORDINATION_EVENT,
      handleCoordination as EventListener
    )

    return () => {
      visibilityObserver.disconnect()
      window.removeEventListener(
        RUN_SAFE_VIDEO_COORDINATION_EVENT,
        handleCoordination as EventListener
      )
    }
  }, [isMobile])

  if (isMobile) {
    return (
      <div ref={containerRef} className='absolute inset-0'>
        <video
          ref={videoRef}
          className='absolute left-0 top-0 h-full w-full object-cover'
          autoPlay
          muted
          playsInline
          loop
          aria-label='Run Safe In The Dark Video'
        >
          <source
            src='https://mcdn.coolmate.me//image/2026/09/15/Shortweb.mp4'
            type='video/mp4'
          />
        </video>
      </div>
    )
  }

  return (
    <div ref={containerRef} className='absolute left-0 top-0 h-full w-full overflow-hidden'>
      {embedSrc && (
        <iframe
          ref={iframeRef}
          className='pointer-events-none absolute left-1/2 top-1/2 h-[56.25vw] min-h-full w-[177.78vh] min-w-full -translate-x-1/2 -translate-y-1/2'
          src={embedSrc}
          title='Run Safe In The Dark Video'
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          allowFullScreen
        />
      )}
    </div>
  )
}
